import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin } from "lucide-react";

interface LocationContextProps {
  locationName: string;
  occupationTitle: string;
  medianSalary?: number;
  meanSalary?: number;
  employment?: number;
  p10?: number;
  p90?: number;
  hourlyMean?: number;
  isNational?: boolean;
}

const currency = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(n));

/**
 * Location-specific context for salary pages. Uses the page's own wage figures
 * so every occupation × location combination produces distinct prose.
 */
export default function LocationContext({
  locationName,
  occupationTitle,
  medianSalary,
  meanSalary,
  employment,
  p10,
  p90,
  hourlyMean,
  isNational = false,
}: LocationContextProps) {
  const median = medianSalary || meanSalary;
  const mean = meanSalary || medianSalary;

  if (!median && !mean) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Salary Context for {locationName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">
            Salary levels for {occupationTitle} in {locationName} are influenced by
            cost of living, local demand, industry concentration, and regional
            economic conditions. Use the Regional Price Parity (RPP) toggle on this
            page when comparing purchasing power across states.
          </p>
        </CardContent>
      </Card>
    );
  }

  const marketSentence = isNational
    ? `National figures average across high-cost coastal metros and lower-cost inland markets, so individual state pages on Wage Atlas are usually the better reference for a specific offer.`
    : `Compared with the national distribution, local industry mix and cost of living are the main reasons ${locationName} wages for this occupation sit where they do — toggle Regional Price Parity on this page to see purchasing-power-adjusted pay.`;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          {occupationTitle} pay in {locationName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-muted-foreground leading-relaxed">
          In {locationName}, {occupationTitle.toLowerCase()} report a median of{' '}
          <strong className="text-foreground">{currency(median!)}</strong>
          {mean && mean !== median ? (
            <>
              {' '}
              and an average of <strong className="text-foreground">{currency(mean)}</strong>
            </>
          ) : null}
          {hourlyMean && hourlyMean > 0 ? <> (about ${hourlyMean.toFixed(2)}/hour)</> : null}
          {employment && employment > 0 ? (
            <>
              {' '}
              across roughly {employment.toLocaleString('en-US')} jobs
            </>
          ) : null}
          .
          {p10 && p90 ? (
            <>
              {' '}
              The local pay band runs from about {currency(p10)} at the 10th percentile
              to {currency(p90)} at the 90th.
            </>
          ) : null}
        </p>
        <p className="text-muted-foreground leading-relaxed text-sm">
          {marketSentence}
        </p>
      </CardContent>
    </Card>
  );
}
